import { z } from 'zod';
import { MoneySchema } from './common.js';
// Decimal columns serialise as canonical strings; accept either on the wire.
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
const DecimalLikeNonNeg = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const PpeMovementSnapshotStatusSchema = z.enum(['DRAFT', 'FINALIZED']);
// ============================================================
// PAE PP&E categories — 6 buckets. The frontend prototype shows 7 rows
// (FURN + CIP added on top); the API folds those into the canonical 6.
// FURN → OTHER, CIP → OTHER (until Phase 4+ when CIP gets its own model).
// ============================================================
export const PpeCategorySchema = z.enum([
    'LAND',
    'BUILDING',
    'MACHINERY',
    'VEHICLE',
    'COMPUTER',
    'OTHER',
]);
// ============================================================
// PpeMovementSnapshot — frozen reconciliation row
// ============================================================
export const PpeMovementSnapshotSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    category: z.string().min(1),
    openingCost: DecimalLikeNonNeg,
    additions: DecimalLikeNonNeg,
    disposals: DecimalLikeNonNeg,
    transfersIn: DecimalLikeNonNeg,
    transfersOut: DecimalLikeNonNeg,
    closingCost: DecimalLikeNonNeg,
    openingAccDep: DecimalLikeNonNeg,
    depCurrent: DecimalLikeNonNeg,
    depReversal: DecimalLikeNonNeg,
    closingAccDep: DecimalLikeNonNeg,
    openingNbv: DecimalLike,
    closingNbv: DecimalLike,
    status: PpeMovementSnapshotStatusSchema,
    generatedAt: z.coerce.date(),
    generatedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// POST /ppe-movement/generate
// ============================================================
export const GeneratePpeMovementSchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
export const GeneratePpeMovementResultSchema = z.object({
    period: z.string(),
    snapshotCount: z.number().int().nonnegative(),
    skippedFinalized: z.number().int().nonnegative(),
    snapshots: z.array(PpeMovementSnapshotSchema),
});
// ============================================================
// GET /ppe-movement/snapshots — filters
// ============================================================
export const PpeMovementListFiltersSchema = z.object({
    period: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
    category: z.string().optional(),
    status: PpeMovementSnapshotStatusSchema.optional(),
    // When compute=true, the service bypasses the persisted snapshots and
    // recomputes on the fly from FixedAsset + DepreciationEntry. Useful for
    // "live numbers" during period-close cross-check.
    compute: z.coerce.boolean().optional(),
});
// ============================================================
// GET /ppe-movement/summary?year=YYYY — 12-month grid by category
// ============================================================
export const PpeMovementMonthCellSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    closingNbv: DecimalLike,
    closingCost: DecimalLikeNonNeg,
    closingAccDep: DecimalLikeNonNeg,
    status: PpeMovementSnapshotStatusSchema.nullable(),
});
export const PpeMovementSummaryRowSchema = z.object({
    category: z.string(),
    months: z.array(PpeMovementMonthCellSchema),
});
export const PpeMovementSummarySchema = z.object({
    year: z.number().int(),
    rows: z.array(PpeMovementSummaryRowSchema),
});
//# sourceMappingURL=ppe-movement.js.map